import {Injectable, PLATFORM_ID, Inject} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {ApiService} from "./api.service";
import {UserService} from "./user.service";
import {get_school_id, SchoolService} from "./school.service";
import {environment} from "../../environments/environment";
import {SessionWarningService} from "./session-warning.service";
import {SsrCookieService} from "./ssr-cookie.service";

interface LoginPayload {
	email: string;
	password: string;
}

@Injectable({
	providedIn: 'root'
})
export class AuthService {
	private is_logged_in_var = false;
	private _token: string = null;
	public jwt_obj = null;
	private _device_token = null;
	public require_login_callback = null;
	public is_admin_panel = false;
	private session_warning_timeout: any = null;
	private isBrowser: boolean;

	constructor(
		@Inject(PLATFORM_ID) platformId: Object,
		private api: ApiService,
		public user: UserService,
		public school:SchoolService,
		private sessionWarning: SessionWarningService,
		private ssrCookie: SsrCookieService,
	) {
		this.isBrowser = isPlatformBrowser(platformId);
		this.init_token();
	}

	init_token() {
		if (this.isBrowser) {
			// Browser: read from localStorage (primary) and sync with cookie
			this.token = localStorage.getItem(this.token_key) || null;
			this.device_token = localStorage.getItem('device_token') || this.uuid_v4();
			
			// Sync localStorage token to cookie for SSR
			if (this.token) {
				this.ssrCookie.setAuthToken(this.token, this.getTokenMaxAge());
			}
		} else {
			// Server: read from cookie (passed from browser request)
			const cookieToken = this.ssrCookie.getAuthToken();
			if (cookieToken) {
				this._token = cookieToken;
				this.api.token = cookieToken;
				this.jwt_obj = this.decode_jwt_token(cookieToken);
			}
		}

		this.is_logged_in_var = !!this._token;
	}
	
	/**
	 * Get the token max age based on JWT expiry
	 */
	private getTokenMaxAge(): number {
		if (!this.jwt_obj || !this.jwt_obj.expires) {
			// Default: 7 days
			return 7 * 24 * 60 * 60;
		}
		const currentTime = Math.floor(Date.now() / 1000);
		return Math.max(0, this.jwt_obj.expires - currentTime);
	}


	get token_key() {
		if (this.is_admin_panel) {
			return `online_shop_${get_school_id()}_admin_token`;
		}
		return `online_shop_${get_school_id()}_token`;
	}

	set token(value) {
		this._token = value;
		if (!value) {
			this.api.token = null;
			if (this.isBrowser) {
				localStorage.removeItem(this.token_key);
				// Also remove from cookie
				this.ssrCookie.removeAuthToken();
			}
			this.jwt_obj = null;
			// Clear session warning timeout
			if (this.session_warning_timeout) {
				clearTimeout(this.session_warning_timeout);
				this.session_warning_timeout = null;
			}
		} else {
			this.api.token = value;
			this.jwt_obj = this.decode_jwt_token(value);
			if (this.isBrowser) {
				localStorage.setItem(this.token_key, value);
				// Also set in cookie for SSR
				this.ssrCookie.setAuthToken(value, this.getTokenMaxAge());
			}
			// Schedule token management for the new token
			this.schedule_token_management();
		}
	}

	get token() {
		return this._token;
	}

	set device_token(value) {
		this._device_token = value;
		if (this.isBrowser) {
			localStorage.setItem('device_token', value);
		}
	}

	get device_token() {
		return this._device_token;
	}

	get isStudent() {
		return this.user.user_role === 'student';
	}

	get isTeacher() {
		return this.user.user_role === 'teacher';
	}

	async admin_login(email: string, password: string): Promise<boolean> {
		this.is_admin_panel = true;
		this.user.is_admin = true;
		return this._login('no admin login available', {email, password});
	}

	async login(payload: LoginPayload): Promise<boolean> {
		// return false
		// temp disable old login API
		return this._login('ROOnlineShopUser.POST_login', payload);
	}

	async _login(api: string, payload: LoginPayload) {

		if (!payload.email || !payload.password) {
			return false;
		}

		let response = await this.api.post(api, {
			...payload,
			token: this.device_token,
			deviceType: this.get_device_type(),
			loginDomain: environment.loginDomain,
			tag_id: environment.online_shop_tag_id
		});
		if (response.code !== 0) {
			return false;
		}

		if (`${response.account.school_id}` !== `${environment.online_shop_school_id}`) {
			return false;
		}

		if (response.account.jwt) {
			this.is_logged_in_var = true; // 假設驗證成功
			this.token = response.account.jwt;
			this.user.set_display_name(`${response.account.c_last_name}${response.account.c_first_name}`);
			this.user.set_profile_image_url(response.account.profile_pic_url ? `${response.account.profile_pic_url}` : `/assets/img/avatar_student_male.svg`);
			this.user.set_email(response.account.email);
			this.user.set_user_role(parseInt(response.account.user_role) === 2 ? 'student' : parseInt(response.account.user_role) === 3 ? 'teacher' : '');
		}
		return this.is_logged_in_var;
	}

	async admin_logout(): Promise<void> {
		// 在這裡實現用戶登出邏輯
		// 將 isLoggedInVar 設置為 false
		// TODO: [LEGACY API] Migrate to new API pattern (Namespace.method)
		// this.api.post('/api/admin/user/logout');
		this.is_logged_in_var = false;
		this.token = null;
		this.user.clear_data();
	}

	async logout(): Promise<void> {
		// 在這裡實現用戶登出邏輯
		// 將 isLoggedInVar 設置為 false
		// TODO: [LEGACY API] Migrate to new API pattern (Namespace.method)
		// this.api.post('/api/user/logout');
		this.is_logged_in_var = false;
		this.token = null; // This will also clear timeouts via the setter
		this.user.clear_data();
	}

	is_logged_in(): boolean {
		// 返回 isLoggedInVar 的值
		return this.is_logged_in_var;
	}

	decode_jwt_token(token: string): any {
		let payload = token.split('.')[1];
		payload = atob(payload);
		return JSON.parse(payload);
	}

	/**
	 * Check if the current token is expired
	 */
	is_token_expired(): boolean {
		if (!this.jwt_obj || !this.jwt_obj.expires) {
			return true;
		}
		const currentTime = Math.floor(Date.now() / 1000);
		return currentTime >= this.jwt_obj.expires;
	}

	/**
	 * Get time remaining until token expires (in seconds)
	 */
	get_token_time_remaining(): number {
		if (!this.jwt_obj || !this.jwt_obj.expires) {
			return 0;
		}
		const currentTime = Math.floor(Date.now() / 1000);
		return Math.max(0, this.jwt_obj.expires - currentTime);
	}

	/**
	 * Check if should show session warning based on configurable intervals
	 */
	should_show_session_warning(): boolean {
		const timeRemaining = this.get_token_time_remaining();
		if (timeRemaining <= 0) return false;
		
		// Warning intervals: 1 hour, 30 min, 15 min, 5 min
		const warningIntervals = [3600, 1800, 900, 300]; // seconds
		
		// Debug intervals for testing: 6d23h57m, 6d, 5d, 4d, 3d, 2d, 1d
		// const debugIntervals = [
		// 	604620, // 6 days 23 hours 57 minutes
		// 	518400, // 6 days
		// 	432000, // 5 days
		// 	345600, // 4 days
		// 	259200, // 3 days
		// 	172800, // 2 days
		// 	86400   // 1 day
		// ];
		
		// Check if we're in a warning window (within any interval but not in a smaller one)
		const allIntervals = [/*...debugIntervals, */...warningIntervals].sort((a, b) => b - a);
		
		for (let i = 0; i < allIntervals.length; i++) {
			const interval = allIntervals[i];
			const nextInterval = allIntervals[i + 1] || 0;
			
			if (timeRemaining <= interval && timeRemaining > nextInterval) {
				return true;
			}
		}
		
		return false;
	}



	async get_user_profile(): Promise<any> {
		// 在這裡實現獲取用戶資料邏輯
		// 如果用戶已經登入，返回用戶資料
		// 如果用戶未登入，返回 null
		// TODO: [LEGACY API] Migrate to new API pattern (Namespace.method)
		// Original code commented out - API endpoint is broken
		/*
		if (this.is_logged_in()) {
			let response: any = await this.api.post('/api/user/profile');
			if (response.success) {
				return response;
			}
		}
		*/
		return null;
	}

	uuid_v4() {
		return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
			let r = Math.random() * 16 | 0,
				v = c === 'x' ? r : (r & 0x3 | 0x8);

			return v.toString(16);
		});
	}

	get_device_type() {
		if (!this.isBrowser) return "Server";
		
		if (/iPad/.test(navigator.userAgent))
			return "iPad";
		if (/iPhone/.test(navigator.userAgent))
			return "iPhone";
		if (/Android/.test(navigator.userAgent))
			return "Android";
		if (/Windows/.test(navigator.userAgent))
			return "Windows";
		if (/Mac/.test(navigator.userAgent))
			return "Mac";
		return "Other";
	}

	public require_login() {
		if (this.require_login_callback) {
			return this.require_login_callback();
		}
	}

	async login_with_ro_jwt(ro_jwt) {
		// TODO: [LEGACY API] Migrate to new API pattern (Namespace.method)
		// Original code commented out - API endpoint is broken
		/*
		let response = await this.api.post('/api/admin/user/login_with_ro_jwt', {
			ro_jwt: ro_jwt,
		});

		if (response.token) {
			this.is_logged_in_var = true; // 假設驗證成功
			this.token = response.token;
			this.user.set_display_name(response.display_name);
			this.user.set_profile_image_url(response.profile_image_url);
			this.user.set_email(response.uacc_email);
		}

		return this.is_logged_in_var;
		*/
		// Placeholder until new API is implemented
		console.warn('[LEGACY API] login_with_ro_jwt not available - pending migration');
		return false;
	}

	async is_token_valid(is_admin = false) {
		// TODO: [LEGACY API] Migrate to new API pattern (Namespace.method)
		// Original code commented out - API endpoint is broken
		/*
		let response = await this.api.post('/api/user/is_token_valid', {
			is_admin: is_admin,
		});
		return response.success;
		*/
		// Placeholder until new API is implemented
		return false;
	}



	/**
	 * Enhanced login status check with expiration validation
	 */
	is_logged_in_and_valid(): boolean {
		if (!this.is_logged_in_var || !this.token) {
			return false;
		}

		// Check if token is expired
		if (this.is_token_expired()) {
			console.warn('Token is expired, logging out user');
			this.logout();
			return false;
		}

		return true;
	}

	/**
	 * Schedule session warnings at appropriate intervals
	 */
	private schedule_token_management(): void {
		// Clear existing timeout
		if (this.session_warning_timeout) {
			clearTimeout(this.session_warning_timeout);
			this.session_warning_timeout = null;
		}

		if (!this.jwt_obj || !this.jwt_obj.expires) {
			return;
		}

		this.schedule_next_warning();
	}

	/**
	 * Schedule the next session warning
	 */
	private schedule_next_warning(): void {
		const timeRemaining = this.get_token_time_remaining();
		
		if (timeRemaining <= 0) {
			return;
		}
		
		// All warning intervals: debug + production
		const allIntervals = [
			// 604620, // 6 days 23 hours 57 minutes
			// 518400, // 6 days
			// 432000, // 5 days
			// 345600, // 4 days
			// 259200, // 3 days
			// 172800, // 2 days
			// 86400,  // 1 day
			3600,   // 1 hour
			1800,   // 30 minutes
			900,    // 15 minutes
			300     // 5 minutes
		].sort((a, b) => b - a);
		
		// Find the next warning interval that's less than current time remaining
		const nextWarningInterval = allIntervals.find(interval => interval < timeRemaining);
		
		if (nextWarningInterval) {
			const warningTime = (timeRemaining - nextWarningInterval) * 1000;
			this.session_warning_timeout = setTimeout(() => {
				this.show_session_warning();
				// Schedule the next warning after this one
				this.schedule_next_warning();
			}, warningTime);
		}
	}

	/**
	 * Show session expiration warning using Event-based toast approach
	 */
	private show_session_warning(): void {
		if (!this.isBrowser) return;
		
		const timeRemaining = this.get_token_time_remaining();
		const formattedTime = this.formatTimeRemaining(timeRemaining);
		
		// Emit custom event for toast notification (Event-based approach)
		window.dispatchEvent(new CustomEvent('session-warning', {
			detail: {
				message: `Your session will expire in ${formattedTime}. Please log in again to continue.`,
				timeRemaining,
				formattedTime,
				type: 'error'
			}
		}));
		
		// Show warning through sessionWarning service for other subscribers
		this.sessionWarning.showWarning(timeRemaining);
		
		// Trigger login dialog after a short delay
		setTimeout(() => {
			this.require_login();
		}, 2000);
		
		console.warn(`🔔 Session Warning: Your session will expire in ${formattedTime} (${timeRemaining} seconds)`);
	}

	/**
	 * Format time remaining in a user-friendly way
	 */
	private formatTimeRemaining(seconds: number): string {
		if (seconds >= 86400) {
			const days = Math.floor(seconds / 86400);
			const hours = Math.floor((seconds % 86400) / 3600);
			if (hours > 0) {
				return `${days}d ${hours}h`;
			}
			return `${days}d`;
		} else if (seconds >= 3600) {
			const hours = Math.floor(seconds / 3600);
			const minutes = Math.floor((seconds % 3600) / 60);
			if (minutes > 0) {
				return `${hours}h ${minutes}m`;
			}
			return `${hours}h`;
		} else if (seconds >= 60) {
			const minutes = Math.floor(seconds / 60);
			return `${minutes}m`;
		} else {
			return `${seconds}s`;
		}
	}
}
